import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./client";

export interface Membership {
  slug: string;
  name: string;
  role: string;
  joined_at: string;
}

export interface User {
  id: number;
  email: string;
  display_name: string;
  role: "user" | "admin" | string;
  status: "active" | "blocked" | string;
  marketing_consent: boolean;
  /** Használati statisztika bekapcsolva (alapból igen; profil-kapcsoló — tájékoztató 4.16). */
  usage_stats: boolean;
  credit_balance: number;
  /** A felhasználó projekt-tagságai (rendszerterv 4.). */
  projects: Membership[];
}

export interface RegisterInput {
  display_name: string;
  email: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
  marketing_consent: boolean;
  /** Melyik projektbe regisztrál — a tagság ezzel jön létre. */
  project: string;
  /** A projekt onboarding-kérdéseinek válaszai (a könyvnél: `reader_declared`). */
  answers: Record<string, unknown>;
  /** Honnan jött (pl. `qr`): csak névtelen napi számlálóba kerül. */
  source?: string;
}

/** `unknown_email`: nincs ilyen fiók — a felület regisztrációt ajánl (nem vált át magától). */
export type RequestCodeStatus = "code_sent" | "unknown_email";

export interface RequestCodeResult {
  status: RequestCodeStatus;
  /** Csak dev-módban (DEV_LOGIN_CODE): a fix belépőkód, hogy az űrlap előre kitölthesse. */
  dev_code?: string | null;
}

export const profileApi = {
  /** A név szabadon átírható. */
  setName: (display_name: string) =>
    api<{ user: User }>("/auth/me", { method: "PATCH", body: { display_name } }),
  /** E-mail-csere 1/2: kód az ÚJ címre. */
  requestEmailChange: (email: string) =>
    api<{ ok: boolean }>("/auth/me/email", { method: "POST", body: { email } }),
  /** E-mail-csere 2/2: az új címre küldött kóddal. */
  confirmEmailChange: (email: string, code: string) =>
    api<{ user: User }>("/auth/me/email", { method: "PUT", body: { email, code } }),
  /** Fiók-törlés 1/2: kód a jelenlegi címre. */
  requestDeletion: () => api<{ ok: boolean }>("/auth/me/delete", { method: "POST" }),
  /** Fiók-törlés 2/2: végleges törlés. */
  confirmDeletion: (code: string) =>
    api<{ ok: boolean }>("/auth/me", { method: "DELETE", body: { code } }),
};

export const authApi = {
  me: () => api<{ user: User }>("/auth/me"),
  requestCode: (email: string, project?: string) =>
    api<RequestCodeResult>("/auth/request-code", { method: "POST", body: { email, project } }),
  register: (input: RegisterInput) =>
    api<RequestCodeResult>("/auth/register", { method: "POST", body: input }),
  verify: (email: string, code: string) =>
    api<{ user: User }>("/auth/verify", { method: "POST", body: { email, code } }),
  logout: () => api<{ ok: boolean }>("/auth/logout", { method: "POST" }),
};

export const ME_KEY = ["me"] as const;

/** Az aktuális felhasználó; 401 esetén `null` (nem hiba). */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return (await authApi.me()).user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Hírlevél-feliratkozás ki/be a profilból (a leiratkozás e-mailből is megy, token-linkkel). */
export function useSetSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscribed: boolean) =>
      api<User>("/newsletter/subscription", { method: "PUT", body: { subscribed } }),
    onSuccess: (user) => qc.setQueryData(ME_KEY, user),
  });
}

/** Profil-műveletek: a `me` gyorsítótár a válaszból frissül. */
export function useSetName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: profileApi.setName,
    onSuccess: (r) => qc.setQueryData(ME_KEY, r.user),
  });
}

export function useRequestEmailChange() {
  return useMutation({ mutationFn: profileApi.requestEmailChange });
}

export function useConfirmEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      profileApi.confirmEmailChange(email, code),
    onSuccess: (r) => qc.setQueryData(ME_KEY, r.user),
  });
}

export function useRequestDeletion() {
  return useMutation({ mutationFn: profileApi.requestDeletion });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: profileApi.confirmDeletion,
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });
}
